import { useState } from "react";
import { useAuth } from "../context/AuthContext";
import { KeyRound } from "lucide-react";

export default function AdminSettings() {
  const { authFetch } = useAuth();
  const [currentPassword, setCurrentPassword] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");

  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setSuccess("");

    if (newPassword !== confirmPassword) {
      setError("New passwords do not match.");
      return;
    }

    if (newPassword.length < 6) {
      setError("New password must be at least 6 characters long.");
      return;
    }

    try {
      setLoading(true);
      const response = await authFetch("https://api.elitecarephysiorehab.com/api/users/change-password", {
        method: "PUT",
        body: JSON.stringify({ currentPassword, newPassword }),
      });

      const result = await response.json();

      if (!response.ok) {
        throw new Error(result.message || "Failed to change password");
      }

      setSuccess("Password updated successfully.");
      setCurrentPassword("");
      setNewPassword("");
      setConfirmPassword("");
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div>
      <style>{`
        * { box-sizing: border-box; }
        .settings-header {
          border-bottom: 1px solid #e2e8f0;
          padding-bottom: 14px;
          margin-bottom: 20px;
        }

        .settings-header h2 {
          font-size: 22px;
          font-weight: 800;
          color: #0f172a;
          margin: 0;
        }

        .settings-card {
          width: 100%;
          max-width: 500px;
          background: #f8fafc;
          border: 1px solid #e2e8f0;
          border-radius: 18px;
          padding: 24px;
        }

        .settings-card h3 {
          margin: 0 0 18px;
          font-size: 17px;
          font-weight: 800;
          color: #0f172a;
        }

        .form-group {
          display: flex;
          flex-direction: column;
          margin-bottom: 18px;
        }

        .form-label {
          font-size: 13px;
          font-weight: 800;
          color: #475569;
          margin-bottom: 6px;
        }

        .input-wrap {
          position: relative;
        }

        .input-icon {
          position: absolute;
          top: 50%;
          left: 12px;
          transform: translateY(-50%);
          color: #16a34a;
          width: 16px;
          height: 16px;
        }

        .settings-input {
          width: 100%;
          border: 1px solid #cbd5e1;
          border-radius: 12px;
          padding: 10px 12px 10px 38px;
          font-size: 14px;
          color: #0f172a;
          background: #fff;
          outline: none;
        }

        .submit-btn {
          border: none;
          background: linear-gradient(135deg, #15803d 0%, #16a34a 100%);
          color: #fff;
          padding: 12px;
          border-radius: 12px;
          font-size: 14px;
          font-weight: 800;
          cursor: pointer;
          width: 100%;
        }

        .alert-box {
          border-radius: 12px;
          padding: 12px;
          font-size: 13px;
          margin-bottom: 18px;
        }

        .alert-error {
          background: #fef2f2;
          border: 1px solid #fecaca;
          color: #b91c1c;
        }

        .alert-success {
          background: #f0fdf4;
          border: 1px solid #bbf7d0;
          color: #15803d;
        }

        @media (max-width: 480px) {
          .settings-card {
            max-width: 100%;
            padding: 16px;
          }
          .settings-header h2 {
            font-size: 18px;
          }
        }
      `}</style>

      <div className="settings-header">
        <h2>Settings</h2>
      </div>

      <div className="settings-card">
        <h3>Change Password</h3>

        {error && <div className="alert-box alert-error">{error}</div>}
        {success && <div className="alert-box alert-success">{success}</div>}

        <form onSubmit={handleSubmit}>
          <div className="form-group">
            <label className="form-label">Current Password</label>
            <div className="input-wrap">
              <KeyRound className="input-icon" />
              <input
                type="password"
                className="settings-input"
                placeholder="Enter current password"
                value={currentPassword}
                onChange={(e) => setCurrentPassword(e.target.value)}
                required
              />
            </div>
          </div>

          <div className="form-group">
            <label className="form-label">New Password</label>
            <div className="input-wrap">
              <KeyRound className="input-icon" />
              <input
                type="password"
                className="settings-input"
                placeholder="Enter new password (min 6 chars)"
                value={newPassword}
                onChange={(e) => setNewPassword(e.target.value)}
                required
              />
            </div>
          </div>

          <div className="form-group">
            <label className="form-label">Confirm New Password</label>
            <div className="input-wrap">
              <KeyRound className="input-icon" />
              <input
                type="password"
                className="settings-input"
                placeholder="Confirm new password"
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                required
              />
            </div>
          </div>

          <button type="submit" className="submit-btn" disabled={loading}>
            {loading ? "Updating..." : "Update Password"}
          </button>
        </form>
      </div>
    </div>
  );
}
